import { getAllParts1, getAllParts2, landUrlFragment, legendUrlFragment, overlayDirectUrlFragment, recordedTourUrlFragment, routeCategoriesUrlFragment, routeUrlFragment, seasonsUrlFragment, segmentUrlFragment, slowupLegendUrlFragment, tourUrlFragment, toursUrlFragment, } from './urlHandling/urlParams.js';
import { getLayersForLayertree, hasFilterUrlParam, } from './urlHandling/urlSearchParams.js';
import { disableMapKvp$, modal$, season$, stpShortTitles$, updateChosenFilters$, } from './stores/store-main.js';
import { noHTML, normalizeURL } from './urlHandling/urlNormalizer.js';
import { languageCodes } from './i18n.js';
import { findTopicTabFromHash, fetchStaticPageOrTopic, } from './apis/api-static-pages-and-topics.js';
import { resolveDeployPrefix } from './initialization/deployPrefixResolver.js';
import { apiCodeToLandCode, landCodeToApiCode, seasonFromLandCode, } from './layers/layerHelpers.js';
import { defaultLanguage } from './unspecific/languageUtils.js';
import { bgLayer$, visibleAdditionalRouteLayers$, visibleLayers$, } from './stores/store-map.js';
import { monitorLoadingError } from './initialization/obsoleteApi.js';
import { LAND_CATEGORIES, landCategoryUrlParams, summerLandLayers, winterLandLayers, } from './layers/layerConstants.js';
import { addLayerToStoredSelection, applyStoredMapState, applyStoredRouteState, getStoredLayersForSeason, readStoredMapState, switchActiveSeason, } from './helpers/mapStateStorage.js';
const deployPrefix = resolveDeployPrefix();
const langPart = `:lang(${languageCodes.join('|')})`;
const landPart = `:land(${getAllParts2(landUrlFragment).join('|')})`;
const categoryPart = `:category(${getAllParts2(routeCategoriesUrlFragment).join('|')})`;
const seasonPart = `:season(${getAllParts2(seasonsUrlFragment).join('|')})`;
const routePart = `:routePrefix(${getAllParts1(routeUrlFragment).join('|')})-:routeNumber(\\d+)`;
const segmentPart = `:segmentPrefix(${getAllParts1(segmentUrlFragment).join('|')})-:segmentNumber(\\d+)`;
const layerPart = `:layer(${getAllParts2(overlayDirectUrlFragment).join('|')})-:layerId(\\d+)`;
const layerPartWithTitle = `:layer(${getAllParts2(overlayDirectUrlFragment).join('|')})-:layerId(\\d+)-:title(.*)`;
const legendPart = `:legend(${getAllParts1(legendUrlFragment).join('|')})`;
const slowupLegendPart = `:slowupLegend(${getAllParts1(slowupLegendUrlFragment).join('|')})`;
const toursPart = `:toursPart(${getAllParts1(toursUrlFragment).join('|')})`;
const tourPart = `:tourPart(${getAllParts1(tourUrlFragment).join('|')})`;
const recordedTourPart = `:recordedTourPart(${getAllParts1(recordedTourUrlFragment).join('|')})`;
const defaultSeasonCode = season$.value;
let isFirstRender = true;
window.addEventListener('vaadin-router-location-changed', (event) => {
    if (!isFirstRender)
        modal$.next(undefined);
    isFirstRender = false;
    const hasFilter = hasFilterUrlParam();
    updateChosenFilters$.next(hasFilter);
});
function getNormalizedPathResult(pathname) {
    const url = new URL(pathname, document.location.toString());
    return normalizeURL(url.toString(), defaultLanguage, defaultSeasonCode, deployPrefix);
}
function scheduleCleanUrlParams(context, paramsToDelete, newHash) {
    const search = new URLSearchParams(context.search);
    let changed = false;
    for (const param of paramsToDelete) {
        if (search.has(param)) {
            search.delete(param);
            changed = true;
        }
    }
    const currentHash = context.hash ?? '';
    if (newHash !== undefined && currentHash !== newHash) {
        changed = true;
    }
    if (changed) {
        window.addEventListener('vaadin-router-location-changed', () => {
            const url = new URL(context.pathname, window.location.origin);
            url.search = search.toString();
            url.hash = newHash !== undefined ? newHash : currentHash;
            window.history.replaceState(null, '', url.toString());
        }, { once: true });
    }
}
function isEmbedRequest(context) {
    return new URLSearchParams(context.search).has('embed');
}
function resolveActiveSeason(options) {
    return options.bagSeason
        ? options.bagSeason
        : options.storedSeason && options.useStoredDefaults
            ? options.storedSeason
            : options.fallbackSeason;
}
function applySeasonAndOptionalLand(bag, useSeasonSwitcher = true) {
    const season = bag.season ?? season$.value;
    if (useSeasonSwitcher) {
        switchActiveSeason(season);
    }
    else {
        season$.next(season);
    }
    if (bag.land) {
        addLayerToStoredSelection(landCodeToApiCode[bag.land]);
    }
}
async function createLandListPage(options) {
    const { PageLandList } = await monitorLoadingError(import('./pages/page-land-list.js'));
    const page = new PageLandList();
    if (options?.land)
        page.land = options.land;
    if (options?.category)
        page.category = options.category;
    if (options?.layerToShow)
        page.layerToShow = options.layerToShow;
    return page;
}
const directOverlayLinkAction = async (context) => {
    if (isEmbedRequest(context))
        return iframeAction(context);
    const search = new URLSearchParams(context.search);
    const { bag } = getNormalizedPathResult(context.pathname);
    const { PageMap } = await monitorLoadingError(import('./pages/page-map.js'));
    const page = new PageMap();
    page.keepLayers = context.search?.includes('keepLayers');
    const stored = readStoredMapState();
    const useStoredDefaults = !search.get('layers')?.length;
    const activeSeason = resolveActiveSeason({
        bagSeason: bag.season,
        storedSeason: stored?.season,
        useStoredDefaults,
        fallbackSeason: season$.value,
    });
    if (activeSeason !== season$.value) {
        season$.next(activeSeason);
    }
    if (stored) {
        applyStoredRouteState(stored, {
            activeSeason,
            applyVisibleLayers: useStoredDefaults,
            applyBgLayer: useStoredDefaults,
            applyAdditionalRouteLayers: useStoredDefaults,
        });
    }
    if (bag.overlayType !== 'travelreport') {
        page.layerToHighlight = {
            id: bag.overlayContentId,
            layerName: bag.overlayType,
        };
    }
    page.allowZoomOnPOI(true);
    return page;
};
const iframeAction = async (context) => {
    const url = new URL(context.pathname, document.location.toString());
    url.search = context.search;
    const { bag, kind } = normalizeURL(url.toString(), defaultLanguage, defaultSeasonCode, deployPrefix);
    season$.next(bag.season);
    const { PageMapEmbed } = await monitorLoadingError(import('./pages/page-map-embed.js'));
    const page = new PageMapEmbed();
    if (context.search?.includes('noNavigation'))
        page.noNavigation = true;
    if (bag.embed === 'simple')
        page.hideHeaderFooter = true;
    if (context.search?.includes('onlyRoute'))
        page.onlyRoute = true;
    visibleLayers$.next(getLayersForLayertree(url.searchParams));
    if (bag.tourId) {
        page.tourId = bag.tourId;
        page.tourType = kind === 'recordedtrack' ? 'recordedtrack' : 'normal';
    }
    if (bag.overlayType !== 'travelreport' &&
        bag.overlayType &&
        bag.overlayContentId) {
        page.layerToHighlight = {
            id: bag.overlayContentId,
            layerName: bag.overlayType,
        };
    }
    if (bag.routeNumber) {
        const land = landCodeToApiCode[bag.land];
        page.layerToHighlight = {
            routeNumber: bag.routeNumber,
            layerName: land,
        };
        if (bag.segmentNumber)
            page.layerToHighlight.segmentNumber = bag.segmentNumber;
    }
    if (bag.neutralMapParams?.crosshair)
        page.crosshair = bag.neutralMapParams.crosshair;
    if (bag.neutralMapParams) {
        visibleAdditionalRouteLayers$.next({
            detours: bag.neutralMapParams.detours,
            photos: bag.neutralMapParams.photos,
            logos: bag.neutralMapParams.logos,
            shooting: bag.neutralMapParams.shooting,
        });
    }
    return page;
};
const routeSegmentAction = async (context) => {
    const search = new URLSearchParams(context.search);
    if (search.has('embed')) {
        return iframeAction(context);
    }
    const { bag } = getNormalizedPathResult(context.pathname);
    disableMapKvp$.next(true);
    const stored = readStoredMapState();
    const useStoredDefaults = !search.get('layers')?.length;
    season$.next(bag.season);
    if (bag.land)
        addLayerToStoredSelection(landCodeToApiCode[bag.land]);
    if (stored) {
        applyStoredRouteState(stored, {
            activeSeason: bag.season,
            applyVisibleLayers: useStoredDefaults,
            applyBgLayer: useStoredDefaults,
            applyAdditionalRouteLayers: useStoredDefaults,
        });
    }
    const { PageSegment } = await monitorLoadingError(import('./pages/page-segment.js'));
    const page = new PageSegment();
    page.land = bag.land;
    page.routeNumber = bag.routeNumber;
    page.segmentNumber = bag.segmentNumber || 0;
    page.scrollContainer = document.querySelector('#main');
    const noZoom = search.has('noZoom');
    const noScroll = search.has('noScroll');
    const focus = search.get('focus') === 'map';
    page.scrollToTitle = !noZoom && !noScroll && !focus;
    page.mapVisible = noZoom || noScroll || focus;
    page.fitMap = !noZoom || focus;
    page.resetLayers = !stored && !search.has('keepLayers');
    scheduleCleanUrlParams(context, ['noZoom', 'noScroll', 'keepLayers']);
    return page;
};
export const routes = [
    {
        path: `${deployPrefix}/${langPart}/${seasonPart}`,
        action: async (context) => {
            const { bag } = getNormalizedPathResult(context.pathname);
            applySeasonAndOptionalLand(bag);
            return createLandListPage();
        },
    },
    {
        path: `${deployPrefix}/${langPart}/${landPart}`,
        action: async (context) => {
            const { bag } = getNormalizedPathResult(context.pathname);
            applySeasonAndOptionalLand(bag);
            return createLandListPage({ land: bag.land });
        },
    },
    {
        path: `${deployPrefix}/${langPart}/${landPart}/${categoryPart}`,
        action: async (context) => {
            const { bag } = getNormalizedPathResult(context.pathname);
            applySeasonAndOptionalLand(bag);
            return createLandListPage({ land: bag.land, category: bag.routeCategory });
        },
    },
    {
        path: `${deployPrefix}/${langPart}/${landPart}/${routePart}`,
        action: routeSegmentAction,
    },
    {
        path: `${deployPrefix}/${langPart}/${landPart}/${routePart}/${segmentPart}`,
        action: routeSegmentAction,
    },
    {
        path: `${deployPrefix}/${langPart}/map`,
        action: async (context, commands) => {
            if (isEmbedRequest(context))
                return iframeAction(context);
            const { PageMap } = await monitorLoadingError(import('./pages/page-map.js'));
            const url = new URL(context.pathname, document.location.toString());
            url.search = context.search;
            const { bag } = normalizeURL(url.toString(), defaultLanguage, defaultSeasonCode, deployPrefix);
            const page = new PageMap();
            page.fitBbox = bag.neutralMapParams.fitBbox;
            page.keepLayers = bag.neutralMapParams.keepLayers;
            page.crosshair = bag.neutralMapParams.crosshair;
            page.resolution = bag.neutralMapParams.resolution;
            const hasUrlLayers = url.searchParams.has('layers');
            const stored = readStoredMapState();
            const useStoredDefaults = !hasUrlLayers;
            const activeSeason = resolveActiveSeason({
                bagSeason: bag.season,
                storedSeason: stored?.season,
                useStoredDefaults,
                fallbackSeason: season$.value,
            });
            disableMapKvp$.next(false);
            if (bag.season) {
                season$.next(bag.season);
            }
            else if (useStoredDefaults && stored?.season) {
                season$.next(stored?.season);
            }
            if (!page.keepLayers) {
                const layers = hasUrlLayers || !stored
                    ? getLayersForLayertree(url.searchParams)
                    : getStoredLayersForSeason(stored, activeSeason);
                visibleLayers$.next(layers);
                if (!bag.season) {
                    let landLayer = layers.find((l) => [
                        ...summerLandLayers,
                        ...winterLandLayers,
                        ...landCategoryUrlParams,
                    ].includes(l));
                    if (landLayer) {
                        if (!(landLayer in apiCodeToLandCode)) {
                            for (const layer in LAND_CATEGORIES) {
                                const land = layer;
                                if (LAND_CATEGORIES[land].find((cat) => cat.toLowerCase().includes(landLayer))) {
                                    landLayer = land;
                                    break;
                                }
                            }
                        }
                        const landCode = apiCodeToLandCode[landLayer];
                        if (landCode) {
                            season$.next(seasonFromLandCode(landCode));
                        }
                    }
                }
            }
            if (bag.land && (bag.routeNumber || bag.segmentNumber)) {
                page.layerToHighlight = {
                    routeNumber: bag.routeNumber,
                    segmentNumber: bag.segmentNumber,
                    layerName: landCodeToApiCode[bag.land],
                };
            }
            bgLayer$.next(bag.neutralMapParams.bgLayer
                ? bag.neutralMapParams.bgLayer
                : useStoredDefaults && stored?.bgLayer
                    ? stored?.bgLayer
                    : 'pk');
            visibleAdditionalRouteLayers$.next(useStoredDefaults && stored
                ? stored?.additionalRouteLayers
                : {
                    detours: bag.neutralMapParams.detours,
                    photos: bag.neutralMapParams.photos,
                    logos: bag.neutralMapParams.logos,
                    shooting: bag.neutralMapParams.shooting,
                });
            if (stored) {
                applyStoredRouteState(stored, { activeSeason });
            }
            return page;
        },
    },
    {
        path: `${deployPrefix}/${langPart}/${toursPart}`,
        action: async () => {
            applyStoredMapState();
            const { PageMapTours } = await monitorLoadingError(import('./pages/page-map-tours.js'));
            const page = new PageMapTours();
            page.scrollContainer = document.querySelector('#main');
            return page;
        },
    },
    {
        path: `${deployPrefix}/${langPart}/${tourPart}`,
        action: async (context) => {
            applyStoredMapState();
            const { PageMapTour: PageMapTourView } = await monitorLoadingError(import('./pages/page-map-tour.js'));
            disableMapKvp$.next(true);
            const page = new PageMapTourView();
            page.type = 'normal';
            page.scrollContainer = document.querySelector('#main');
            return page;
        },
    },
    {
        path: `${deployPrefix}/${langPart}/${tourPart}/:id(\\d+)`,
        action: async (context) => {
            if (isEmbedRequest(context))
                return iframeAction(context);
            applyStoredMapState();
            const { bag } = getNormalizedPathResult(context.pathname);
            const { PageMapTour: PageMapTourView } = await monitorLoadingError(import('./pages/page-map-tour.js'));
            const poiId = context.search.match(/poiId=(\d+)/);
            const noZoom = context.search?.includes('noZoom');
            const page = new PageMapTourView();
            page.fitMap = !noZoom;
            page.tourId = bag.tourId;
            page.type = 'normal';
            page.activePoiId = poiId ? parseInt(poiId[1]) : undefined;
            page.scrollContainer = document.querySelector('#main');
            return page;
        },
    },
    {
        path: `${deployPrefix}/${langPart}/${recordedTourPart}/:id(\\d+)`,
        action: async (context) => {
            if (isEmbedRequest(context))
                return iframeAction(context);
            applyStoredMapState();
            const { bag } = getNormalizedPathResult(context.pathname);
            const { PageMapRecordedTour } = await monitorLoadingError(import('./pages/page-map-recordedtour.js'));
            const noZoom = context.search?.includes('noZoom');
            const page = new PageMapRecordedTour();
            page.fitMap = !noZoom;
            page.tourId = bag.tourId;
            page.type = 'recordedtrack';
            page.scrollContainer = document.querySelector('#main');
            return page;
        },
    },
    {
        path: `${deployPrefix}/${langPart}/${layerPart}`,
        action: directOverlayLinkAction,
    },
    {
        path: `${deployPrefix}/${langPart}/${layerPartWithTitle}`,
        action: directOverlayLinkAction,
    },
    {
        path: `${deployPrefix}/${langPart}/${landPart}/${layerPart}`,
        action: async (context) => {
            const { bag } = getNormalizedPathResult(context.pathname);
            applySeasonAndOptionalLand(bag, false);
            return createLandListPage({
                land: bag.land,
                layerToShow: { id: bag.overlayContentId, layerName: bag.overlayType },
            });
        },
    },
    {
        path: `${deployPrefix}/${langPart}/${legendPart}`,
        action: async (context) => {
            const { PageLegend } = await monitorLoadingError(import('./pages/page-legend.js'));
            const element = new PageLegend();
            element.kind = 'legend';
            return element;
        },
    },
    {
        path: `${deployPrefix}/${langPart}/${slowupLegendPart}`,
        action: async (context) => {
            const { PageLegend } = await monitorLoadingError(import('./pages/page-legend.js'));
            const element = new PageLegend();
            element.kind = 'slowup-legend';
            return element;
        },
    },
    {
        path: `${deployPrefix}/${langPart}/feedback:ext(.html)?`,
        action: async (context) => {
            if (context.pathname.endsWith('.html'))
                location.pathname = noHTML(context.pathname);
            const { PageFeedback } = await monitorLoadingError(import('./pages/page-feedback.js'));
            return new PageFeedback();
        },
    },
    {
        path: `${deployPrefix}/${langPart}/login`,
        action: async () => {
            const { PageLogin } = await monitorLoadingError(import('./pages/page-login.js'));
            return new PageLogin();
        },
    },
    {
        path: `${deployPrefix}/${langPart}/:key(.+)`,
        action: async (context) => {
            const lang = context.params['lang'];
            const key = context.params['key'];
            try {
                const spt = await fetchStaticPageOrTopic(key, lang);
                stpShortTitles$.next(spt.titleShort);
                const { PageStaticOrTopic } = await monitorLoadingError(import('./pages/page-static-or-topic.js'));
                const sptElement = new PageStaticOrTopic();
                sptElement.spt = spt;
                switch (spt.kind) {
                    case 'static_page': {
                        sptElement.selectedTab = 0;
                        break;
                    }
                    case 'topic': {
                        const hashAndTab = findTopicTabFromHash(spt, context.hash, lang);
                        if (hashAndTab) {
                            sptElement.selectedTab = hashAndTab.selectedTab;
                            scheduleCleanUrlParams(context, [], hashAndTab.hash);
                        }
                        else {
                            sptElement.selectedTab = 0;
                        }
                    }
                }
                return sptElement;
            }
            catch {
                const { PageNotFound } = await monitorLoadingError(import('./pages/page-not-found.js'));
                return new PageNotFound();
            }
        },
    },
    {
        path: `${deployPrefix}/:notFound(.*)`,
        action: async () => {
            const { PageNotFound } = await monitorLoadingError(import('./pages/page-not-found.js'));
            return new PageNotFound();
        },
    },
];
//# sourceMappingURL=router.js.map